import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogContent,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import { useState, useEffect } from "react";
import axios from "axios";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import VisionSpaceDetail from "../components/VisionSpaceDetail";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 同工卡片 - 企业化设计（可点击）
const WorkerCard = styled(Paper)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.25)",
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  borderRadius: "16px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
  padding: theme.spacing(3),
  height: "600px",
  display: "flex",
  flexDirection: "column",
  cursor: "pointer",
  transition: "box-shadow 0.2s ease, border-color 0.2s ease",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: 16,
    pointerEvents: "none",
  },
  "&:hover": {
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.25)",
    borderColor: "rgba(255, 255, 255, 0.25)",
  },
}));

// 头像容器
const AvatarContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "400px",
  borderRadius: "12px",
  overflow: "hidden",
  backgroundColor: "rgba(0, 0, 0, 0.05)",
  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
  marginBottom: theme.spacing(2),
}));

// 头像图片
const AvatarImage = styled("img")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
}));

// 内容容器
const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: theme.spacing(1, 0),
  position: "relative",
  zIndex: 1,
}));

// 整体容器
const GlassContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  background: "rgba(0, 0, 0, 0.25)",
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  borderRadius: "24px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
  transition: "all 0.3s ease",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: 24,
    pointerEvents: "none",
  },
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 48px rgba(0, 0, 0, 0.3)",
    background: "rgba(0, 0, 0, 0.3)",
  },
}));

// 事奉内容卡片 - 企业化设计（可点击）
const MinistryCard = styled(Paper)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.4)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  borderRadius: "16px",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
  padding: theme.spacing(3),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  cursor: "pointer",
  transition: "box-shadow 0.2s ease, border-color 0.2s ease",
  "&:hover": {
    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.25)",
    borderColor: "rgba(255, 255, 255, 0.15)",
  },
}));

// 导航按钮
const NavButton = styled(IconButton)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.9)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  color: "rgba(0, 0, 0, 0.7)",
  width: 48,
  height: 48,
  transition: "all 0.3s ease",
  "&:hover": {
    background: "rgba(255, 255, 255, 1)",
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  "&.Mui-disabled": {
    background: "rgba(255, 255, 255, 0.5)",
    color: "rgba(0, 0, 0, 0.3)",
  },
}));

// 深色毛玻璃效果的对话框 - 企业化设计
const GlassDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: "rgba(0, 0, 0, 0.3)",
    backdropFilter: "blur(15px)",
    WebkitBackdropFilter: "blur(15px)",
    borderRadius: "20px",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
    maxWidth: "1000px",
    width: "90%",
    maxHeight: "90vh",
    padding: 0,
    overflow: "hidden",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(255, 255, 255, 0.05)",
      borderRadius: 20,
      pointerEvents: "none",
    },
  },
}));

// 对话框标题栏
const DialogHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 24px",
  position: "relative",
  zIndex: 1,
}));

// 详情页容器 - 紧凑布局
const DetailContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));

// 详情页左侧信息 - 紧凑设计
const DetailInfo = styled(Box)(({ theme }) => ({
  flex: "0 0 280px",
  position: "relative",
  zIndex: 1,
  [theme.breakpoints.down("md")]: {
    flex: "none",
  },
}));

// 详情页右侧内容 - 紧凑设计
const DetailContent = styled(Box)(({ theme }) => ({
  flex: 1,
  position: "relative",
  zIndex: 1,
}));

// 视频区域容器 - 紧凑设计
const VideoSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: 0,
}));

// 视频容器 - 企业化设计
const VideoContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "400px",
  borderRadius: "12px",
  overflow: "hidden",
  marginTop: theme.spacing(1.5),
  background: "rgba(0, 0, 0, 0.3)",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  "& iframe": {
    width: "100%",
    height: "100%",
    border: "none",
  },
}));

// 获取 YouTube 视频 ID
const getYouTubeVideoId = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

const Serve = () => {
  const [ministryWorkers, setMinistryWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [selectedMinistry, setSelectedMinistry] = useState(null);
  const [failedImages, setFailedImages] = useState(new Set());

  // 获取图片完整URL
  const getImageUrl = (path) => {
    if (!path) return `${API_BASE_URL}/images/avatars/default-avatar.png`;
    return `${API_BASE_URL}${path}`;
  };

  // 处理图片加载错误
  const handleImageError = (workerId, imageUrl) => {
    if (!failedImages.has(workerId)) {
      console.log("头像加载失败:", imageUrl);
      setFailedImages((prev) => new Set([...prev, workerId]));
    }
  };

  useEffect(() => {
    const fetchMinistryWorkers = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/ministry-worker`);
        setMinistryWorkers(response.data);
        setError(null);
      } catch (err) {
        console.error("获取同工数据失败:", err);
        setError("获取同工数据失败，请稍后再试");
      } finally {
        setLoading(false);
      }
    };

    fetchMinistryWorkers();
  }, []);

  const handleWorkerClick = (worker) => {
    setSelectedWorker(worker);
  };

  const handleCloseDialog = () => {
    setSelectedWorker(null);
  };

  // 处理事奉内容卡片点击
  const handleMinistryClick = (ministry) => {
    setSelectedMinistry({ _id: ministry._id });
  };

  // 关闭事奉内容详情
  const handleCloseMinistryDetail = () => {
    setSelectedMinistry(null);
  };

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          pt: 10,
          pb: 8,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          pt: 10,
          pb: 8,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ pt: 6, pb: 10 }}>
      <Box
        sx={{
          textAlign: "center",
          mb: 8,
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: "-16px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "40px",
            height: "2px",
            backgroundColor: "rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: 500,
            mb: 2,
            color: "rgba(255, 255, 255, 0.95)",
            letterSpacing: "0.02em",
            fontSize: { xs: "1.75rem", md: "2rem" },
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
          }}
        >
          事奉同工
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            color: "rgba(255, 255, 255, 0.9)",
            fontWeight: 400,
            maxWidth: "600px",
            mx: "auto",
            letterSpacing: "0.01em",
            lineHeight: 1.6,
            fontSize: { xs: "1rem", md: "1.1rem" },
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
          }}
        >
          了解我们的同工信息，共同参与事奉
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {ministryWorkers.map((worker) => (
          <Grid item xs={6} sm={6} md={3} key={worker._id}>
            <WorkerCard onClick={() => handleWorkerClick(worker)}>
              <AvatarContainer>
                <AvatarImage
                  src={
                    failedImages.has(worker._id)
                      ? `${API_BASE_URL}/images/avatars/default-avatar.png`
                      : getImageUrl(worker.avatar)
                  }
                  alt={worker.name}
                  onError={() => handleImageError(worker._id, worker.avatar)}
                  onLoad={(e) => {
                    console.log("头像加载成功:", worker.avatar);
                  }}
                />
              </AvatarContainer>
              <ContentContainer>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "rgba(255, 255, 255, 0.95)",
                      mb: 1,
                      fontSize: "1.25rem",
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    {worker.name}
                  </Typography>
                  {worker.position && (
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "rgba(255, 255, 255, 0.9)",
                        mb: 1,
                        fontWeight: 500,
                        fontSize: "1rem",
                        textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      {worker.position}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255, 255, 255, 0.85)",
                      lineHeight: 1.6,
                      display: "-webkit-box",
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: "0.95rem",
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    {worker.introduction}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  {worker.email && (
                    <Chip
                      icon={<EmailIcon sx={{ fontSize: "1rem" }} />}
                      label={worker.email}
                      size="small"
                      sx={{
                        borderRadius: "8px",
                        background: "rgba(255, 255, 255, 0.1)",
                        backdropFilter: "blur(4px)",
                        WebkitBackdropFilter: "blur(4px)",
                        color: "rgba(255, 255, 255, 0.9)",
                        "& .MuiChip-label": {
                          color: "rgba(255, 255, 255, 0.9)",
                        },
                      }}
                    />
                  )}
                </Box>
              </ContentContainer>
            </WorkerCard>
          </Grid>
        ))}
      </Grid>

      {/* 同工详情弹窗 */}
      <GlassDialog
        open={Boolean(selectedWorker)}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        {/* 标题栏 */}
        <DialogHeader>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 600,
              color: "rgba(255, 255, 255, 0.95)",
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
            }}
          >
            同工详情
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              color: "rgba(255, 255, 255, 0.7)",
              "&:hover": {
                color: "rgba(255, 255, 255, 0.9)",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogHeader>

        <DialogContent sx={{ p: 3, position: "relative", zIndex: 1 }}>
          {selectedWorker && (
            <DetailContainer>
              {/* 左侧信息 - 紧凑企业化设计 */}
              <DetailInfo>
                <AvatarContainer sx={{ width: "100%", height: "250px", mb: 2 }}>
                  <AvatarImage
                    src={
                      failedImages.has(selectedWorker._id)
                        ? `${API_BASE_URL}/images/avatars/default-avatar.png`
                        : getImageUrl(selectedWorker.avatar)
                    }
                    alt={selectedWorker.name}
                    onError={() =>
                      handleImageError(
                        selectedWorker._id,
                        selectedWorker.avatar
                      )
                    }
                  />
                </AvatarContainer>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 1.5,
                    fontWeight: 600,
                    color: "rgba(255, 255, 255, 0.95)",
                    fontSize: { xs: "1.25rem", md: "1.5rem" },
                    lineHeight: 1.3,
                    textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  {selectedWorker.name}
                </Typography>
                {selectedWorker.position && (
                  <Typography
                    variant="body1"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      mb: 2,
                      fontWeight: 500,
                      fontSize: "0.9rem",
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    {selectedWorker.position}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.9rem",
                    lineHeight: 1.6,
                    color: "rgba(255, 255, 255, 0.8)",
                    mb: 2.5,
                    textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  {selectedWorker.introduction}
                </Typography>

                <Divider
                  sx={{
                    borderColor: "rgba(255, 255, 255, 0.1)",
                    mb: 2,
                  }}
                />

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {selectedWorker.email && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EmailIcon
                        sx={{
                          color: "rgba(255, 255, 255, 0.7)",
                          fontSize: "1rem",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(255, 255, 255, 0.85)",
                          fontSize: "0.9rem",
                          textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                        }}
                      >
                        {selectedWorker.email}
                      </Typography>
                    </Box>
                  )}
                  {selectedWorker.phone && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PhoneIcon
                        sx={{
                          color: "rgba(255, 255, 255, 0.7)",
                          fontSize: "1rem",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(255, 255, 255, 0.85)",
                          fontSize: "0.9rem",
                          textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                        }}
                      >
                        {selectedWorker.phone}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </DetailInfo>

              {/* 右侧内容 - 紧凑企业化设计 */}
              <DetailContent>
                <Box
                  sx={{
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <InfoIcon
                    sx={{
                      color: "rgba(255, 255, 255, 0.7)",
                      fontSize: "1rem",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      color: "rgba(255, 255, 255, 0.9)",
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      letterSpacing: "0.01em",
                    }}
                  >
                    事奉内容
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  {selectedWorker.ministries?.map((ministry) => (
                    <Grid item xs={12} sm={6} md={4} key={ministry._id}>
                      <MinistryCard
                        onClick={() => handleMinistryClick(ministry)}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            mb: 1.5,
                            color: "rgba(255, 255, 255, 0.95)",
                            fontSize: "1rem",
                            textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                          }}
                        >
                          {ministry.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(255, 255, 255, 0.75)",
                            mb: 2,
                            fontSize: "0.85rem",
                            textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                          }}
                        >
                          {new Date(ministry.date).toLocaleDateString()}
                        </Typography>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "4px 12px",
                            borderRadius: "20px",
                            background: "rgba(255, 255, 255, 0.08)",
                            border: "1px solid rgba(255, 255, 255, 0.12)",
                            backdropFilter: "blur(8px)",
                            WebkitBackdropFilter: "blur(8px)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(255, 255, 255, 0.95)",
                              fontWeight: 500,
                              fontSize: "0.75rem",
                            }}
                          >
                            {ministry.status === "upcoming"
                              ? "即将开始"
                              : "已完成"}
                          </Typography>
                        </Box>
                      </MinistryCard>
                    </Grid>
                  ))}
                </Grid>

                {selectedWorker.videoUrl && (
                  <VideoSection>
                    <Box
                      sx={{
                        mb: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <PlayCircleOutlineIcon
                        sx={{
                          fontSize: "1rem",
                          color: "rgba(255, 255, 255, 0.7)",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "0.9rem",
                          fontWeight: 500,
                          color: "rgba(255, 255, 255, 0.9)",
                          textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                          letterSpacing: "0.01em",
                        }}
                      >
                        自我介绍小视频
                      </Typography>
                    </Box>
                    <VideoContainer>
                      <iframe
                        src={`https://www.youtube.com/embed/${getYouTubeVideoId(
                          selectedWorker.videoUrl
                        )}`}
                        title="YouTube video player"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </VideoContainer>
                  </VideoSection>
                )}
              </DetailContent>
            </DetailContainer>
          )}
        </DialogContent>
      </GlassDialog>

      {/* 事奉内容详情弹窗 */}
      <VisionSpaceDetail
        open={Boolean(selectedMinistry)}
        onClose={handleCloseMinistryDetail}
        activity={selectedMinistry}
      />
    </Container>
  );
};

export default Serve;
